import React, { useEffect, useState } from "react";
import submit from "submit-form";
import SvgIcon from "../../../../parts/SvgIcon";
import { useTranslation } from "react-i18next";

const EmptyStoreState = ({
  category,
  currentTab,
  status,
  hasDraftedPrograms,
  hasPublishedPrograms,
}) => {
  const { t } = useTranslation("coaching-portal");
  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    if (currentTab !== "teams") return;
    const getVideoThumbnail = async () => {
      const { oembed } = await import("@loomhq/loom-embed");
      const videoMeta = await oembed(
        "https://www.loom.com/share/491ae39a80784d00bcad79e0c2c86161"
      );

      if (videoMeta) {
        setImageUrl(videoMeta?.thumbnail_url);
      }
    };

    getVideoThumbnail();
  }, [currentTab]);

  if (currentTab === "teams") {
    const openVideoModal = () => {
      submit("/actions/refresh?", {
        method: "GET",
        body: {
          "modal[type]": "watch-loom-video",
          sharedUrl: "491ae39a80784d00bcad79e0c2c86161",
        },
      });
    };

    if (status === "published" && !!hasDraftedPrograms) {
      return (
        <div className="no-teams-section">
          <div className="no-teams-section-card">
            <h3>{t("store.emptyState.teamsFirstAlmostReady")}</h3>

            <p className="p2 py-8 text-center">
              {t("store.emptyState.teamsPublishHint")}
            </p>
            <a
              role="button"
              className="py-2 px-4"
              data-button-type="primary"
              href="/coaching-portal/store?status=draft&storeType=teams"
            >
              {t("store.emptyState.goToDraftTeams")}
            </a>
          </div>
        </div>
      );
    }

    if (status !== "published" && !!hasPublishedPrograms) {
      return (
        <div className="no-programs-section">
          <h3 className="pb-4">{t("store.emptyState.noTeamsDrafted")}</h3>
          <p className="p2 pb-9">{t("store.emptyState.createNewTeam")}</p>
          <a
            role="button"
            data-button-type="primary"
            href={`/actions/refresh/?modal[type]=build-step-one&cycleType=new&isSubscription=true&storeType=${currentTab}`}
          >
            <img
              alt={t("store.emptyState.addAlt")}
              src="/img/new-icons/add.svg"
            />
            {t("store.emptyState.addNew")}
          </a>
        </div>
      );
    }

    return (
      <div className="no-teams-section">
        <div className="no-teams-section-card">
          <h3>{t("store.emptyState.noTeamsYet")}</h3>

          <div className="w-71 h-full">
            {imageUrl && (
              <SvgIcon
                src="/img/new-icons/play-white.svg"
                classNames="play-icon"
                noFilterOnLight={true}
                onClickFn={openVideoModal}
              />
            )}

            {imageUrl && (
              <img
                className="rounded-lg aspect-video object-cover mb-6 cursor-pointer"
                src={imageUrl}
                alt={t("store.emptyState.teamsVideoAlt")}
                onClick={openVideoModal}
              />
            )}
          </div>

          <a
            target="_blank"
            href="https://msbapp.notion.site/team-programs"
            className="mb-6 text-blue-400 underline"
            rel="noopener"
          >
            {t("store.emptyState.teamsGuide")}
          </a>

          <h4 className="pb-3">{t("store.emptyState.teamsHeadline")}</h4>

          <p className="p2 pb-8 text-center">
            {t("store.emptyState.teamsDescription")}
          </p>
          <a
            role="button"
            className="py-2 px-4"
            data-button-type="primary"
            href={`/actions/refresh/?modal[type]=build-step-one&cycleType=new&isSubscription=true&storeType=${currentTab}`}
          >
            <img
              alt={t("store.emptyState.addAlt")}
              src="/img/new-icons/add.svg"
            />
            {t("store.emptyState.createFirstTeam")}
          </a>
        </div>
      </div>
    );
  }

  if (category) {
    const typeKey =
      currentTab === "programs" ? "store.programsLabel" : "store.workoutsLabel";
    const typeSingularKey =
      currentTab === "programs" ? "store.programLabel" : "store.workoutLabel";
    return (
      <div className="no-programs-section">
        <h3 className="pb-4">
          {t("store.emptyState.noProgramsInCategory", {
            type: t(typeKey),
          })}
        </h3>
        <p className="p2 pb-9">
          {t("store.emptyState.addProgramOrWorkout", {
            type: t(typeSingularKey),
          })}
        </p>
        <button
          data-button-type="primary"
          onClick={() => {
            submit("/actions/refresh?", {
              method: "GET",
              body: {
                "modal[type]": "manage-categories",
                storeType: currentTab,
              },
            });
          }}
        >
          <img
            alt={t("store.emptyState.addAlt")}
            src="/img/new-icons/add.svg"
          />
          {t("store.emptyState.manageCategory")}
        </button>
      </div>
    );
  }

  const typeKey =
    currentTab === "programs" ? "store.programsLabel" : "store.workoutsLabel";
  const typeSingularKey =
    currentTab === "programs" ? "store.programLabel" : "store.workoutLabel";
  const statusKey = status === "draft" ? "store.drafted" : "store.published";
  const actionKey = status === "draft" ? "store.create" : "store.publish";
  return (
    <>
      <div className="no-programs-section">
        <h3 className="pb-4">
          {t("store.emptyState.noProgramsDraftedOrPublished", {
            type: t(typeKey),
            status: t(statusKey),
          })}
        </h3>
        <p className="p2 pb-9">
          {t("store.emptyState.createOrPublishNew", {
            action: t(actionKey),
            type: t(typeSingularKey),
          })}
        </p>
        <a
          role="button"
          data-button-type="primary"
          href={`/actions/refresh/?modal[type]=build-step-one&cycleType=new&isSubscription=true&storeType=${currentTab}`}
        >
          <img
            alt={t("store.emptyState.addAlt")}
            src="/img/new-icons/add.svg"
          />
          {t("store.emptyState.addNew")}
        </a>
      </div>
    </>
  );
};

export default EmptyStoreState;
